import React, { useState } from 'react';
import { X } from 'lucide-react';
import { getAuth, signOut, sendEmailVerification } from 'firebase/auth';
import { buildApiUrl, getBackendUrl } from '../utils/api';
import { isAndroidInstalledApp, STORE_BILLING_MESSAGE } from '../utils/storeEnv';
import { clearBotProductionModeCache } from '../utils/botProductionMode';
import { logger } from '../utils/logger';

/** Mesmo estilo do CTA «Fazer upgrade →» nas regras / análise do BotConfig */
const CONTRACT_CTA_BTN_STYLE = (disabled) => ({
  padding: '10px 24px',
  background: disabled ? '#9A9AA5' : '#FFC107', // brand book primary sólido
  color: disabled ? '#fff' : '#0E0E12', // ink sobre amarelo
  border: 'none',
  borderRadius: 9999, // pill
  fontSize: '13px',
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  boxSizing: 'border-box',
});

/**
 * @param {object} props
 * @param {() => Promise<void>} [props.onLimitedSettingsAccess] — Se definido (gate de billing no app), o X leva às configurações,
 *   desliga o bot em produção (quando a API permitir) e libera só /settings; /chat continua bloqueado.
 */
const TrialExpiredModal = ({
  isOpen,
  userEmail,
  subscriptionPlan,
  isAdmin,
  type = 'trial',
  selectedCompany,
  onLimitedSettingsAccess,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [xLoading, setXLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [verifyMsg, setVerifyMsg] = useState('');

  if (!isOpen) return null;

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      window.location.href = '/login';
      await signOut(auth);
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const turnOffBotProduction = async () => {
    const authInstance = getAuth();
    const currentUser = authInstance.currentUser;
    const companyId = selectedCompany?.companyId || selectedCompany?.id;
    if (!currentUser || !companyId) return;

    const authToken = await currentUser.getIdToken(true);
    const now = new Date().toISOString();
    const lastChangedByName =
      currentUser.displayName || currentUser.email || currentUser.uid;

    const response = await fetch(`${getBackendUrl()}/update_bot_production_mode`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        companyId,
        productionMode: false,
        lastChangedBy: currentUser.uid,
        lastChangedAt: now,
        lastChangedByName,
      }),
    });

    if (!response.ok) {
      const errJson = await response.json().catch(() => ({}));
      const msg = errJson.error || `Não foi possível desativar o bot (${response.status}).`;
      throw new Error(msg);
    }
    clearBotProductionModeCache();
  };

  const handleCloseClick = async () => {
    if (onLimitedSettingsAccess) {
      setErrorMsg('');
      setXLoading(true);
      try {
        if (isAdmin) {
          try {
            await turnOffBotProduction();
          } catch (e) {
            logger.warn('[TrialExpiredModal] Desligar bot em produção:', e?.message || e);
          }
        }
        await onLimitedSettingsAccess();
      } finally {
        setXLoading(false);
      }
      return;
    }
    handleLogout();
  };

  // O checkout agora é criado server-side (Stripe Checkout Session com
  // client_reference_id + metadata.company_id + trial_period_days quando aplicável).
  const handlePayment = async () => {
    // 🏪 Google Play: sem checkout dentro do app Android (ver storeEnv.js)
    if (isAndroidInstalledApp()) {
      setErrorMsg(STORE_BILLING_MESSAGE);
      return;
    }
    setErrorMsg('');
    setIsProcessing(true);
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        window.location.href = '/login';
        return;
      }
      const idToken = await currentUser.getIdToken(true);
      const companyId = selectedCompany?.companyId || selectedCompany?.id;

      const response = await fetch(buildApiUrl('/api/checkout-session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + idToken,
        },
        body: JSON.stringify({ plan: subscriptionPlan, companyId }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.url) {
        throw new Error(payload.error || 'Falha ao iniciar checkout');
      }
      window.location.assign(payload.url);
    } catch (err) {
      console.error('Erro ao iniciar checkout:', err);
      setErrorMsg(err.message || 'Não foi possível iniciar o pagamento. Tente novamente.');
      setIsProcessing(false);
    }
  };

  const handleResendVerification = async () => {
    setVerifyMsg('');
    setIsProcessing(true);
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        setVerifyMsg('E-mail de verificação reenviado. Confira sua caixa de entrada (e o spam).');
      }
    } catch (err) {
      setVerifyMsg(err?.message || 'Não foi possível reenviar agora. Tente novamente em alguns minutos.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCheckVerified = async () => {
    setVerifyMsg('');
    setIsProcessing(true);
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        await auth.currentUser.reload();
        await auth.currentUser.getIdToken(true); // atualiza o claim email_verified no token
        if (auth.currentUser.emailVerified) {
          window.location.reload(); // re-roda o check-trial → libera o acesso
          return;
        }
        setVerifyMsg('Ainda não detectamos a verificação. Clique no link do e-mail e tente novamente.');
      }
    } catch (err) {
      setVerifyMsg(err?.message || 'Erro ao verificar. Tente novamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getTitle = () => {
    if (type === 'email_not_verified') return 'Verifique seu e-mail';
    if (type === 'pending_checkout') return 'Finalize seu cadastro';
    if (type === 'subscription_ended') return 'Assinatura encerrada';
    return type === 'trial'
      ? 'Período de avaliação encerrado'
      : 'Pagamento mensal pendente';
  };

  const getMessage = () => {
    if (type === 'email_not_verified') {
      return 'Para liberar seu período gratuito, confirme seu e-mail. Enviamos um link de verificação para a sua caixa de entrada (verifique também o spam). Depois de confirmar, clique em “Já verifiquei”.';
    }
    if (type === 'pending_checkout') {
      return isAdmin
        ? 'Seu cadastro está quase completo. Finalize o checkout para liberar os 7 dias gratuitos e o acesso completo ao painel de atendimento e às automações.'
        : 'O cadastro da empresa está pendente de finalização. O painel de atendimento fica bloqueado até o administrador concluir o checkout.';
    }
    if (type === 'subscription_ended') {
      return isAdmin
        ? 'A assinatura desta empresa foi cancelada e o período já pago chegou ao fim. Para voltar a usar o painel de atendimento, filas, bot no WhatsApp e demais recursos, contrate o plano novamente.'
        : 'A assinatura desta empresa foi cancelada e o período já pago chegou ao fim. O painel de atendimento está bloqueado até o administrador reativar a assinatura.';
    }
    if (type === 'trial') {
      return isAdmin
        ? 'O período de testes desta empresa terminou. Para continuar usando o painel de atendimento, filas, bot no WhatsApp e demais recursos, é necessário assinar o plano.'
        : 'O período de testes desta empresa terminou. O painel de atendimento está bloqueado até o administrador regularizar a assinatura.';
    }
    return isAdmin
      ? 'Há pendência no pagamento da assinatura. Regularize para restaurar o acesso completo à plataforma.'
      : 'Há pendência no pagamento da assinatura. Informe o administrador da empresa para regularizar e liberar o acesso.';
  };

  const getLimitedExitHint = () => {
    if (!onLimitedSettingsAccess) return null;
    return (
      <p className="text-sm text-gray-500 text-left mt-4 mb-0 border-t border-gray-200 pt-4">
        <strong className="text-gray-700">Sobre o botão ✕ acima:</strong> você pode ir para as{' '}
        <strong>configurações</strong> da empresa. O <strong>painel de atendimento (/chat)</strong> continua
        bloqueado até a regularização.
        {isAdmin ? (
          <>
            {' '}
            O <strong>modo de produção do bot</strong> será desativado (o bot deixa de atender no WhatsApp
            nesse modo) e o interruptor fica bloqueado até o pagamento ser confirmado.
          </>
        ) : (
          <>
            {' '}
            Apenas administradores podem desativar o modo de produção automaticamente; fale com o admin da
            empresa se precisar.
          </>
        )}
      </p>
    );
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[rgba(11,11,18,0.72)] backdrop-blur-sm z-50 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-6 relative">
        <button
          type="button"
          onClick={handleCloseClick}
          disabled={xLoading || isProcessing}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          aria-label={
            onLimitedSettingsAccess
              ? 'Ir para configurações (painel de atendimento continua bloqueado)'
              : 'Sair do sistema'
          }
          title={
            onLimitedSettingsAccess
              ? 'Configurações: desativa o bot em produção (se você for admin) e libera só as configurações'
              : undefined
          }
        >
          <X size={24} />
        </button>
        
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-[#0E0E12]">{getTitle()}</h2>

          {type === 'email_not_verified' ? (
            <>
              <p className="text-gray-600 mb-4 text-left leading-relaxed">{getMessage()}</p>
              {userEmail && (
                <p className="text-sm text-gray-500 mb-4 text-left">
                  Enviado para <strong className="text-gray-700">{userEmail}</strong>.
                </p>
              )}
              <div className="flex flex-col items-center gap-3 mt-2 w-full">
                <button
                  type="button"
                  onClick={handleCheckVerified}
                  disabled={isProcessing}
                  style={CONTRACT_CTA_BTN_STYLE(isProcessing)}
                >
                  {isProcessing ? 'Verificando…' : 'Já verifiquei →'}
                </button>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={isProcessing}
                  className="text-sm text-gray-600 underline hover:text-gray-800"
                >
                  Reenviar e-mail de verificação
                </button>
              </div>
              {verifyMsg && (
                <p className="mt-3 text-sm text-gray-700">{verifyMsg}</p>
              )}
            </>
          ) : isAdmin ? (
            <>
              <p className="text-gray-600 mb-4 text-left leading-relaxed">{getMessage()}</p>
              {getLimitedExitHint()}
              
              <div className="mb-6 mt-6">
                <h3 className="text-lg font-semibold mb-2 text-[#0E0E12]">Benefícios do Plano:</h3>
                <ul className="text-left space-y-2 text-gray-600">
                  <li>✓ Acesso completo à plataforma</li>
                  <li>✓ Suporte prioritário</li>
                  <li>✓ Atualizações constantes</li>
                  <li>✓ Mais recursos e funcionalidades</li>
                </ul>
              </div>

              <div className="flex justify-center w-full mt-2">
                <button
                  type="button"
                  onClick={handlePayment}
                  disabled={isProcessing}
                  style={CONTRACT_CTA_BTN_STYLE(isProcessing)}
                >
                  {isProcessing ? 'Abrindo checkout…' : 'Contratar →'}
                </button>
              </div>
              {errorMsg && (
                <p className="mt-3 text-sm text-red-600">{errorMsg}</p>
              )}
            </>
          ) : (
            <>
              <p className="text-gray-600 mb-4 text-left leading-relaxed">{getMessage()}</p>
              {getLimitedExitHint()}
              
              <div className="mb-6 mt-6">
                <h3 className="text-lg font-semibold mb-2 text-[#0E0E12]">O que fazer agora?</h3>
                <ul className="text-left space-y-2 text-gray-600">
                  <li>✓ Entre em contato com o administrador da sua empresa</li>
                  <li>✓ Informe sobre a pendência de pagamento</li>
                  <li>✓ Solicite a realização do pagamento</li>
                </ul>
              </div>
            </>
          )}
          
          <p className="mt-4 text-sm text-gray-500">
            Dúvidas? Entre em contato: <a href="mailto:contato@iatconsulting.com.br" className="text-[#0E0E12] hover:underline">contato@iatconsulting.com.br</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiredModal; 